import { useState, useEffect } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { updateEmployee } from "@/services/users";
import EmployeeModal from "./EmployeeModal";
import GenericTable from "@/components/ui/GenericTable";
import Pagination from "../common/Pagination";

interface EmployeeListProps {
  employees: any[];
  loading: boolean;
  onRefresh: () => void;
  searchQuery: string;
  statusFilter: string;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  isBranchIncharge?: boolean;
  branchCode?: string;
}

export default function EmployeeList({
  employees,
  loading,
  onRefresh,
  searchQuery,
  statusFilter,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
  isBranchIncharge,
  branchCode,
}: EmployeeListProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<any | null>(null);
  const [viewingEmployee, setViewingEmployee] = useState<any | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [localEmployees, setLocalEmployees] = useState<any[]>(employees);

  // Sync local copy whenever the parent refreshes data
  useEffect(() => { setLocalEmployees(employees); }, [employees]);

  const handleToggleActive = async (employee: any) => {
    if (!employee.id || togglingId === employee.id) return;

    const newStatus = !employee.Is_Active;

    // Optimistic update — flip instantly in UI
    setLocalEmployees((prev) =>
      prev.map((e) => e.id === employee.id ? { ...e, Is_Active: newStatus } : e)
    );
    setTogglingId(employee.id);

    try {
      const response = await updateEmployee(Number(employee.id), { Is_Active: newStatus });
      if (response.status === 200) {
        toast.success(`Employee ${newStatus ? "activated" : "deactivated"} successfully`);
        onRefresh()
      } else {
        // Revert on failure
        setLocalEmployees((prev) =>
          prev.map((e) => e.id === employee.id ? { ...e, Is_Active: employee.Is_Active } : e)
        );
        toast.error("Failed to update employee status");
      }
    } catch {
      // Revert on error
      setLocalEmployees((prev) =>
        prev.map((e) => e.id === employee.id ? { ...e, Is_Active: employee.Is_Active } : e)
      );
      toast.error("Error updating employee status");
    } finally {
      setTogglingId(null);
    }
  };


  const handleAdd = () => {
    setEditingEmployee(null);
    setIsModalOpen(true);
  };

  const handleEdit = (employee: any) => {
    setEditingEmployee(employee);
    setIsModalOpen(true);
  };

  const getRoleBadgeColor = (role: string) => {
    const roleMap: Record<string, string> = {
      "Admin": "bg-brand-100 text-brand-700",
      "Zone Incharge": "bg-purple-100 text-purple-700",
      "Tele Caller": "bg-green-100 text-green-700",
      "Delivery Boy": "bg-orange-100 text-orange-700",
    };
    return roleMap[role] || "bg-gray-100 text-gray-700";
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      render: (_: any, row: any) => (
        <div>
          <p className="font-bold text-gray-900 text-base">{row.Emp_Name}</p>
          <p className="text-sm text-gray-500">{row.Emp_No}</p>
        </div>
      ),
    },
    {
      key: "Email",
      label: "Email",
    },
    {
      key: "Phone",
      label: "Phone",
    },
    {
      key: "Role",
      label: "Role",
      render: (value: any) => (
        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getRoleBadgeColor(value)}`}>
          {value}
        </span>
      ),
    },
    {
      key: "Branch",
      label: "Branch",
      render: (value: any) => Array.isArray(value) ? (value.join(", ") || "N/A") : (value || "N/A"),
    },
    {
      key: "status",
      label: "Status",
      render: (_: any, row: any) => (
        <button
          onClick={() => handleToggleActive(row)}
          disabled={togglingId === row.id}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer disabled:opacity-50 ${
            row.Is_Active ? "bg-green-500" : "bg-red-500"
          }`}
          title={row.Is_Active ? "Active - Click to deactivate" : "Inactive - Click to activate"}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              row.Is_Active ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      ),
    },
  ];

  return (
    <>
      <div className="space-y-6">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          itemsPerPage={20}
          onPageChange={onPageChange}
        />
        <GenericTable
          buttonName="Add Employee"
          handleAdd={handleAdd}
          title='Staff'
          columns={columns}
          data={localEmployees}
          loading={loading}
          onView={setViewingEmployee}
          onEdit={handleEdit}
          emptyMessage="No employees found"
        />
      </div>

      {/* Employee Modal */}
      {isModalOpen && (
        <EmployeeModal
          key={editingEmployee?.id ?? 'new'}
          employee={editingEmployee}
          onClose={() => { setIsModalOpen(false); setEditingEmployee(null); }}
          onSuccess={() => {
            setIsModalOpen(false);
            onRefresh();
          }}
          isBranchIncharge={isBranchIncharge}
          branchCode={branchCode}
        />
      )}

      {/* View Employee Modal */}
      {viewingEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Employee Details</h3>
              <button
                onClick={() => setViewingEmployee(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-500">Employee Number</p>
                <p className="text-gray-900 text-base">{viewingEmployee.Emp_No}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Employee Name</p>
                <p className="text-gray-900 text-base">{viewingEmployee.Emp_Name}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Username</p>
                <p className="text-gray-900 text-base">{viewingEmployee.Username}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Email</p>
                <p className="text-gray-900 text-base">{viewingEmployee.Email}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Phone</p>
                <p className="text-gray-900 text-base">{viewingEmployee.Phone || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Emergency Contact</p>
                <p className="text-gray-900 text-base">{viewingEmployee.Emergency_Contact || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Role</p>
                <p className="text-gray-900 text-base">{viewingEmployee.Role}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Branch</p>
                <p className="text-gray-900 text-base">{Array.isArray(viewingEmployee.Branch) ? (viewingEmployee.Branch.join(", ") || "N/A") : (viewingEmployee.Branch || "N/A")}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Zone</p>
                <p className="text-gray-900 text-base">{viewingEmployee.Zone || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">State</p>
                <p className="text-gray-900 text-base">{viewingEmployee.State || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">City</p>
                <p className="text-gray-900 text-base">{viewingEmployee.City || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Date of Birth</p>
                <p className="text-gray-900 text-base">
                  {viewingEmployee.Date_Of_Birth ? new Date(viewingEmployee.Date_Of_Birth).toLocaleDateString() : "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Join Date</p>
                <p className="text-gray-900 text-base">
                  {viewingEmployee.Join_Date ? new Date(viewingEmployee.Join_Date).toLocaleDateString() : "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Is Admin</p>
                <p className="text-gray-900 text-base">{viewingEmployee.Is_Admin ? "Yes" : "No"}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm font-semibold text-gray-500">Address</p>
                <p className="text-gray-900 text-base">{viewingEmployee.Address || "N/A"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Status</p>
                <p className="text-gray-900 text-base">{viewingEmployee.Is_Active ? "Active" : "Inactive"}</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-500">Created At</p>
                <p className="text-gray-900 text-base">
                  {viewingEmployee.Created_At ? new Date(viewingEmployee.Created_At).toLocaleDateString() : "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
